import React from "react";

import {
  Box,
  Checkbox,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { Settings } from "@mui/icons-material";

import { getNuclide, isNuclideIdentical } from "core/services/nuclideService";
import NuclideCanvas from "../core/NuclideCanvas";
import useCustomCanvas from "core/hooks/useCustomCanvas";
import CanvasActions from "./CanvasActions";
import { CanvasBackgroundContainer, CanvasContainer } from "./shared";

const NuclideGraph = (props) => {
  const {
    defaultSizeSetting,
    selectedNuclide,
    setSelectedNuclide,
    setFullScreenGraph,
    isFullScreen,
  } = props;
  const ref = React.useRef();

  const [nuclideCanvas, size, defaultSize] = useCustomCanvas(
    defaultSizeSetting,
    () => ({
      ref,
      myCanvasInstance: new NuclideCanvas({
        selectedTarget: selectedNuclide,
        ctx: ref.current.getContext("2d"),
        shouldDrawDecayChain: true,
        shouldDrawCross: true,
        shouldDrawSecondaryChain: true,
        onSelect: (proton, neutron) => {
          const state = { proton, neutron };
          const selected = getNuclide(state);
          setSelectedNuclide(selected);
          return selected;
        },
      }),
    })
  );

  React.useEffect(() => {
    if (
      nuclideCanvas &&
      !isNuclideIdentical(selectedNuclide, nuclideCanvas?.selectedTarget) &&
      nuclideCanvas?.initiated
    ) {
      nuclideCanvas?.setProp("currentDecayChain", null);
      if (selectedNuclide)
        nuclideCanvas?.reTarget(
          selectedNuclide.neutron,
          selectedNuclide.proton
        );
      nuclideCanvas?.setProp("selectedTarget", selectedNuclide, true);
    }
  }, [selectedNuclide, nuclideCanvas]);

  return (
    <CanvasBackgroundContainer>
      <CanvasContainer className="unselectable" width={size.width}>
        <canvas className="unselectable border-radius" ref={ref} />
      </CanvasContainer>
      {nuclideCanvas?.initiated && (
        <NuclideGraphOptions
          nuclideCanvas={nuclideCanvas}
          isFullScreen={isFullScreen}
          setFullScreenGraph={setFullScreenGraph}
          defaultSize={defaultSize}
        />
      )}
    </CanvasBackgroundContainer>
  );
};

const NuclideGraphOptions = React.memo(
  ({ nuclideCanvas, isFullScreen, setFullScreenGraph, defaultSize }) => {
    const handleSetCheckboxProp = (e) => {
      const { name, checked } = e.target;
      nuclideCanvas?.setProp(name, checked, true);
    };
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);
    const handleClick = (event) => {
      setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
      setAnchorEl(null);
    };

    return (
      <Stack direction="row" alignItems="center">
        <Typography
          variant="subtitle2"
          mx="0.5em"
          sx={{ display: { xs: "none", md: "inline" } }}
        >
          Nuclide Graph
        </Typography>

        <Box flexGrow="1" />
        <CanvasActions
          type="NuclideGraph"
          myCanvas={nuclideCanvas}
          isFullScreen={isFullScreen}
          setFullScreenGraph={setFullScreenGraph}
          defaultSize={defaultSize}
        />
        <Tooltip arrow title="Settings">
          <IconButton onClick={handleClick} color="inherit">
            <Settings />
          </IconButton>
        </Tooltip>
        <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
          <ListItem dense>
            <ListItemIcon>
              <Checkbox
                edge="start"
                onClick={handleSetCheckboxProp}
                name="shouldDrawCoord"
                defaultChecked={Boolean(nuclideCanvas?.shouldDrawCoord)}
                tabIndex={-1}
              />
            </ListItemIcon>
            <ListItemText primary="Show Coordinates" />
          </ListItem>
          <ListItem dense>
            <ListItemIcon>
              <Checkbox
                edge="start"
                onClick={handleSetCheckboxProp}
                name="shouldDrawCross"
                defaultChecked={Boolean(nuclideCanvas?.shouldDrawCross)}
                tabIndex={-1}
              />
            </ListItemIcon>
            <ListItemText primary="Show Cross" />
          </ListItem>
          <ListItem dense>
            <ListItemIcon>
              <Checkbox
                edge="start"
                onClick={handleSetCheckboxProp}
                name="shouldDrawDecayChain"
                defaultChecked={Boolean(nuclideCanvas?.shouldDrawDecayChain)}
                tabIndex={-1}
              />
            </ListItemIcon>
            <ListItemText primary="Show Decay Chain" />
          </ListItem>
          <ListItem dense>
            <ListItemIcon>
              <Checkbox
                edge="start"
                onClick={handleSetCheckboxProp}
                name="shouldDrawSecondaryChain"
                defaultChecked={Boolean(
                  nuclideCanvas?.shouldDrawSecondaryChain
                )}
                tabIndex={-1}
              />
            </ListItemIcon>
            <ListItemText primary="Show Secondary Decay Chain" />
          </ListItem>
        </Menu>
      </Stack>
    );
  }
);

export default React.memo(NuclideGraph);
